import { z } from "zod";
import { Id, IsoDate, Locale, Metric, QueryBool, SeriesPoint } from "./common.ts";

// ---------------------------------------------------------------- identity
export const Role = z.enum(["owner", "admin", "editor", "viewer"]);
export const User = z.object({ id: Id, email: z.string().email(), name: z.string(), locale: Locale, avatarUrl: z.string().url().nullable(), platformRole: z.enum(["platform_admin"]).nullable().optional(), /** ALP-85: chosen at sign-up (access request); null for accounts created by staff */ username: z.string().nullable().optional() }); // platformRole: Malfak staff (admin area at /admin)
/** `plan` = the assigned plan key (ALP-81: trial | free | pro | business); `onboarding` = done per org (ALP-79). */
export const Org = z.object({ id: Id, name: z.string(), slug: z.string(), plan: z.enum(["trial", "free", "pro", "business"]), role: Role, onboarding: z.object({ state: z.enum(["pending", "finished", "skipped"]), completedAt: IsoDate.nullable() }).optional() });
export const Member = z.object({ user: User, role: Role, joinedAt: IsoDate });
export const Me = z.object({ user: User, orgs: z.array(Org), currentOrgId: Id.nullable() });
export const LoginRequest = z.object({ email: z.string().email(), password: z.string().min(8) });
export const SignupRequest = LoginRequest.extend({ name: z.string().min(1), orgName: z.string().min(1) });

// ---------------------------------------------------------------- channels
export const Channel = z.enum(["instagram", "facebook_page", "messenger", "whatsapp", "threads", "tiktok"]);
export const ChannelCapabilities = z.object({
  publishTypes: z.array(z.enum(["image", "video", "carousel", "reel", "story", "text"])),
  maxCarouselItems: z.number().nullable(),
  publishQuotaPer24h: z.number().nullable(),
  comments: z.object({ reply: z.boolean(), hide: z.boolean(), delete: z.boolean(), privateReplyWindowDays: z.number().nullable() }).nullable(),
  messaging: z
    .object({
      freeTextWindowHours: z.number().nullable(),
      outsideWindow: z.enum(["none", "message_tag", "template"]),
      quickReplies: z.boolean(),
      maxButtons: z.number(),
      templates: z.boolean(),
      attachments: z.array(z.enum(["image", "video", "audio", "file"])),
    })
    .nullable(),
  insights: z.array(z.string()),
  webhooks: z.array(z.string()),
});
export const ChannelAccount = z.object({
  id: Id,
  channel: Channel,
  kind: z.string(),
  externalId: z.string(),
  username: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  status: z.enum(["active", "needs_reauth", "disconnected", "syncing"]),
  followers: z.number().nullable(),
  capabilities: ChannelCapabilities,
  webhook: z.object({
    subscribed: z.boolean(),
    fields: z.array(z.string()),
    subscribedAt: IsoDate.nullable().optional(), // ALP-77: when /me/subscribed_apps succeeded
    error: z.string().nullable().optional(), // why the subscription failed (account stays connected)
  }),
  aiEnabled: z.boolean(),
  connectedAt: IsoDate,
  lastSyncedAt: IsoDate.nullable(),
  /** ALP-77: when the stored long-lived token expires (refreshed daily within 10 days of expiry). */
  tokenExpiresAt: IsoDate.nullable().optional(),
  /** ALP-77: the latest initial-import (backfill) job for this account; null once nothing was ever imported. */
  backfill: z.object({ jobId: Id, status: z.enum(["queued", "running", "paused", "succeeded", "failed", "cancelled"]) }).nullable().optional(),
});
// ---------------------------------------------------------------- Instagram Login (ALP-77)
/** `GET /v1/oauth/instagram/start?orgId=…` (browser navigation; 302 to instagram.com). */
export const OAuthStartQuery = z.object({ orgId: Id });
/** Meta's redirect back: `code` + `state` on success; `error`, `error_reason`, `error_description` when the person cancels. */
export const OAuthCallbackQuery = z.object({
  code: z.string().max(2048).optional(),
  state: z.string().max(4096).optional(),
  error: z.string().max(200).optional(),
  error_reason: z.string().max(200).optional(),
  error_description: z.string().max(1000).optional(),
});
/** Codes the callback appends as `/settings/channels?error=<code>` (the web shows a friendly message per code). */
export const OAUTH_ERROR_CODES = [
  "access_denied", "state_invalid", "state_expired", "state_reused", "forbidden", "exchange_failed", "profile_failed",
  "not_professional", "account_conflict", "not_configured", "server_error",
  // the app is in Development mode and the Instagram account has no tester role on it (or hasn't accepted the invite)
  "not_tester",
] as const;
export const OAuthErrorCode = z.enum(OAUTH_ERROR_CODES);
/** A 302: `location` carries the target; the body is informational only. */
export const OAuthRedirect = z.object({ redirectUrl: z.string() });
/** Meta deauthorize / data-deletion callbacks (application/x-www-form-urlencoded). */
export const SignedRequestBody = z.object({ signed_request: z.string().min(3).max(8192) });
export const DataDeletionCode = z.string().regex(/^[A-Za-z0-9]{8,64}$/);
/** Meta requires exactly this response from the data-deletion callback. */
export const DataDeletionCallbackResponse = z.object({ url: z.string().url(), confirmation_code: DataDeletionCode });
/** Public status page `/data-deletion?code=…` (no personal data: counts only). */
export const DataDeletionStatus = z.object({
  code: DataDeletionCode,
  channel: Channel,
  status: z.enum(["received", "in_progress", "completed", "failed"]),
  requestedAt: IsoDate,
  completedAt: IsoDate.nullable(),
  summary: z
    .object({ accounts: z.number(), posts: z.number(), comments: z.number(), conversations: z.number(), messages: z.number(), contacts: z.number() })
    .nullable(),
});
/** ALP-77: progress of the initial import (operations.result of a `backfill_account` job), updated after every page. */
export const BackfillStage = z.enum(["profile", "insights", "media", "messages", "done"]);
/**
 * Why a post's comments are incomplete after the import (QA F8):
 *   not_returned — Instagram counts them but `/{media}/comments` does not list them (verified live: an empty `data` for a
 *                  post with comments_count 8; restricted commenters and comments held for review are not returned);
 *   api_error    — reading the post's comments failed (detail = the error).
 */
export const BackfillGapReason = z.enum(["not_returned", "api_error"]);
export const BackfillGap = z.object({
  media: z.string(),
  expected: z.number(),
  stored: z.number(),
  missing: z.number(),
  reason: BackfillGapReason,
  detail: z.string().nullable(),
});
export const BackfillProgress = z.object({
  stage: BackfillStage,
  posts: z.object({ done: z.number(), total: z.number().nullable() }),
  comments: z.object({ done: z.number(), total: z.number().nullable() }),
  conversations: z.number(),
  messages: z.number(),
  insights: z.boolean(),
  failed: z.number(),
  rateLimitedWaits: z.number(),
  /**
   * QA F8: comments the channel counts (`comments_count`, incl. replies) but the import could not store, summed over
   * every post. > 0 = the import finished with a partial result (the job says "N comments not fetched: <reason>").
   */
  commentsMissing: z.number().optional(),
  /** One entry per post with a shortfall (capped at 50). */
  gaps: z.array(BackfillGap).optional(),
});

export const ConnectAccountRequest = z.discriminatedUnion("method", [
  z.object({ method: z.literal("oauth"), channel: Channel }),
  z.object({ method: z.literal("token"), channel: Channel, accessToken: z.string().min(20) }), // dev/test only
]);

// ---------------------------------------------------------------- data lifecycle primitives (ALP-65, plan 14 §6)
/** Who removed an item on the channel. Instagram never says, so channel-side deletions are usually `unknown`. */
export const DeletedBy = z.enum(["author", "account", "platform", "unknown"]);
/** How Malfak learned about the deletion: a webhook, the reconciliation sync, or Malfak itself deleted it. */
export const DeletionSource = z.enum(["webhook", "reconcile", "malfak"]);
/** How an item entered Malfak: live webhook, initial/backfill sync, recovered by reconciliation, or written by Malfak. */
export const IngestSource = z.enum(["webhook", "sync", "recovered", "malfak", "backfill"]); // backfill = initial import after connect (ALP-77): never automated
/** Built-in flags; custom org tags live next to them (same key space, see Tag). */
export const BUILT_IN_FLAGS = ["spam", "important", "follow_up", "lead", "complaint", "vip"] as const;
export const BuiltInFlag = z.enum(BUILT_IN_FLAGS);
/** A flag or tag key: built-in flag name or a custom tag key (lower-case slug). */
export const FlagKey = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,39}$/, "lower-case letters, digits, _ and - (max 40)");
export const FlaggableEntity = z.enum(["comment", "conversation", "contact"]);
/** ALP-72: how the team marked a commenter (drives the rule condition `commenter_marked`). */
export const CommenterMark = z.enum(["harmful", "troll", "vip"]);
/** ALP-72: what the channel legally lets us know about a person (Business Discovery or the IG user profile API). */
export const ContactProfile = z.object({
  username: z.string().nullable(),
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(), // our object-storage copy (channel CDN URLs expire)
  followers: z.number().nullable(),
  mediaCount: z.number().nullable(),
  biography: z.string().nullable(),
  website: z.string().nullable(),
  verified: z.boolean().nullable(),
  followsBusiness: z.boolean().nullable(),
  businessFollows: z.boolean().nullable(),
  source: z.enum(["business_discovery", "user_profile", "none"]), // none = nothing available for this person
  fetchedAt: IsoDate,
  note: z.string().nullable(), // e.g. "personal account: Business Discovery only covers business/creator accounts"
});
/**
 * People moderation: "Block in Malfak". Instagram's API has NO block or restrict endpoint (Instagram Login and Facebook
 * Login alike), so the real block is only possible in the Instagram app (the UI deep-links to the profile). Malfak's block:
 * existing comments are hidden (job), future comments are auto-hidden on ingest (before rules, run reason
 * `blocked_contact`), DMs still arrive and are kept but are muted (out of the default inbox, no notifications, no AI or
 * auto-replies), and the person is excluded from automation (except rules that use the `commenter_blocked` condition,
 * which still never reply or call AI). `accountId` null = every account of the org; otherwise only that account.
 */
export const ContactBlockState = z.object({
  at: IsoDate,
  by: z.string().nullable(), // user id (or `rule:<id>`)
  byName: z.string().nullable(), // display name of the user who blocked (null when unknown / not a user)
  reason: z.string().nullable(),
  accountId: Id.nullable(),
});
/** Run reason / comment hidden reason written when Malfak acts because the author is blocked. */
export const BLOCKED_CONTACT_REASON = "blocked_contact";

// ---------------------------------------------------------------- contacts
export const Contact = z.object({
  id: Id,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  identities: z.array(z.object({ channel: Channel, externalId: z.string(), handle: z.string().nullable() })),
  tags: z.array(z.string()),
  firstSeenAt: IsoDate,
  lastSeenAt: IsoDate,
  flags: z.array(z.string()).optional(), // built-in flags + custom tag keys (ALP-65); `tags` stays the free-form rule label list
  mark: CommenterMark.nullable().optional(), // ALP-72
  profile: ContactProfile.nullable().optional(), // ALP-72 enrichment
  block: ContactBlockState.nullable().optional(), // Block in Malfak (null = not blocked)
});

// ---------------------------------------------------------------- content
export const MediaAsset = z.object({
  id: Id,
  url: z.string(),
  mime: z.string(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  durationSec: z.number().nullable(),
  // image editing (ALP-81 follow-up): edits and editor exports are NEW assets that point at their source (version history)
  source: z.enum(["upload", "ai", "ai_edit", "editor", "channel", "demo"]).optional(), // demo = seeded demo artwork (generated, never a real post)
  sourceMediaId: Id.nullable().optional(),
  version: z.number().int().optional(),
  createdAt: IsoDate.optional(),
  // ---- channel media mirroring (ALP-77 media fix): imported media are copies in OUR storage (CDN URLs expire in days)
  /** false = not copied yet: `url` is the channel's (expiring) CDN URL; the web shows a placeholder when it breaks + asks a refresh. */
  mirrored: z.boolean().optional(),
  /** image | video. A video's `url` is its poster (thumbnail) image unless the video file itself was mirrored. */
  kind: z.enum(["image", "video"]).optional(),
  /** Video only: our copy of the file when mirrored (MEDIA_MIRROR_VIDEOS), else the channel's reference URL (expires). */
  videoUrl: z.string().nullable().optional(),
  /** The channel's media id (IG media or carousel child id). */
  externalId: z.string().nullable().optional(),
  altText: z.string().nullable().optional(),
});
export const PublicationStatus = z.enum(["draft", "scheduled", "publishing", "published", "failed", "cancelled"]);
export const Publication = z.object({
  id: Id,
  accountId: Id,
  channel: Channel,
  status: PublicationStatus,
  externalId: z.string().nullable(),
  permalink: z.string().nullable(),
  scheduledAt: IsoDate.nullable(),
  publishedAt: IsoDate.nullable(),
  error: z.string().nullable(),
  metrics: z.array(Metric),
  commentsCount: z.number(),
  likesCount: z.number().nullable(),
  aiEnabled: z.boolean(),
  postId: Id.optional(), // the Malfak post this publication belongs to (web: post preview from an inbox comment)
});
export const Post = z.object({
  id: Id,
  type: z.enum(["image", "video", "carousel", "reel", "story", "text"]),
  caption: z.string(),
  media: z.array(MediaAsset),
  status: z.enum(["draft", "in_review", "approved", "scheduled", "published", "partially_published", "failed"]),
  publications: z.array(Publication),
  createdAt: IsoDate,
  createdBy: Id.nullable(),
  imported: z.boolean(), // true = synced from the channel, not created in Malfak
  /** Channel media copy state (imported posts): mirrored = every item is in our storage; pending/failed = `media` may hold the CDN fallback (mirrored: false). */
  mediaStatus: z.enum(["mirrored", "pending", "failed", "none"]).optional(),
  /** Set when a sync/reload saw the caption or media change on Instagram ("edited on Instagram" badge). */
  editedOnChannelAt: IsoDate.nullable().optional(),
  /** post_versions: the version shown now (null = never versioned) and how many exist. */
  currentVersionId: Id.nullable().optional(),
  versionCount: z.number().int().optional(),
});
export const CreatePostRequest = z.object({
  type: Post.shape.type,
  caption: z.string().max(2200),
  mediaIds: z.array(Id).max(10),
  targets: z.array(z.object({ accountId: Id, captionOverride: z.string().max(2200).optional() })).min(1),
  scheduledAt: IsoDate.nullable(), // null = publish now
});

// ---------------------------------------------------------------- engagement
export const AiLabels = z.object({
  intent: z.enum(["question", "price", "purchase", "collab", "complaint", "praise", "spam", "hate", "other"]).nullable(),
  sentiment: z.enum(["positive", "neutral", "negative"]).nullable(),
  toxicity: z.number().min(0).max(1).nullable(),
  language: z.string().nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  /** ALP-83: the org's custom AI categories this item was classified into (key = tag key of a category, confidence 0..1). */
  categories: z.array(z.object({ key: FlagKey, confidence: z.number().min(0).max(1) })).max(20).optional(),
  /** ALP-83: confidence of the sentiment label (0..1); absent = use `confidence`. */
  sentimentConfidence: z.number().min(0).max(1).nullable().optional(),
});
export const InboxState = z.enum(["open", "done", "snoozed"]);
/**
 * What happened to a comment and who did it (ALP-84): read-only, derived from jobs, rule runs, the audit log and channel
 * deletions. `actor.type`: user (a teammate) · rule (an automation rule) · agent (an AI agent) · instagram (removed on the
 * platform) · author (the commenter deleted it) · system (Malfak itself, e.g. hidden because the author is blocked).
 */
export const CommentActor = z.object({
  type: z.enum(["user", "rule", "agent", "instagram", "author", "system"]),
  id: z.string().nullable(),
  name: z.string().nullable(),
});
export const CommentEventKind = z.enum(["hidden", "unhidden", "deleted", "replied", "private_reply", "tagged", "untagged", "archived", "unarchived", "pinned", "unpinned", "rule"]);
export const CommentEvent = z.object({
  kind: CommentEventKind,
  actor: CommentActor,
  at: IsoDate,
  /** The tag key (tagged), or what a rule did in words-free form (its action key, e.g. "hide", "reply"). */
  detail: z.string().nullable().optional(),
  /** The automation run behind a rule event (opens its Automation log entry). */
  runId: Id.nullable().optional(),
});
export const Comment = z.object({
  id: Id,
  accountId: Id,
  channel: Channel,
  publicationId: Id.nullable(),
  externalId: z.string(),
  parentExternalId: z.string().nullable(),
  /** `blocked`: the author is blocked in Malfak for this comment's account (a badge wherever the name appears). */
  author: z.object({ externalId: z.string(), username: z.string(), contactId: Id.nullable(), blocked: z.boolean().optional() }),
  text: z.string(),
  status: z.enum(["visible", "hidden", "deleted"]),
  /** Why Malfak hid it: `blocked_contact` = because the author is blocked (unblock + unhide reverts only these). */
  hiddenReason: z.string().nullable().optional(),
  inboxState: InboxState,
  assigneeId: Id.nullable(),
  labels: AiLabels,
  repliesCount: z.number(),
  byAccount: z.boolean(), // written by the connected account itself
  createdAt: IsoDate,
  privateReplySentAt: IsoDate.nullable().optional(), // Instagram allows one private reply per comment (within 7 days)
  // ---- data lifecycle (ALP-65): the text is never removed; a deleted comment keeps everything plus these markers
  deletedOnChannelAt: IsoDate.nullable().optional(),
  deletedBy: DeletedBy.nullable().optional(),
  deletionSource: DeletionSource.nullable().optional(),
  source: IngestSource.optional(), // "recovered" = found by reconciliation (webhook was missed)
  flags: z.array(z.string()).optional(), // built-in flags + custom tag keys
  // ---- moderation (ALP-72): Malfak-side states (Instagram has no pin API); archived items leave default views
  archivedAt: IsoDate.nullable().optional(),
  pinnedAt: IsoDate.nullable().optional(),
  /** The latest thing that happened to it (hidden / deleted / replied / a rule acted…) and who did it (ALP-84). */
  lastAction: CommentEvent.nullable().optional(),
});
/** One stored version of a DM: the original text, every edit, and the unsend tombstone (text kept). */
export const MessageVersion = z.object({
  version: z.number().int(),
  kind: z.enum(["original", "edit", "unsend"]),
  text: z.string().nullable(),
  at: IsoDate,
  source: z.enum(["webhook", "reconcile"]),
});
export const Message = z.object({
  id: Id,
  conversationId: Id,
  direction: z.enum(["in", "out"]),
  text: z.string().nullable(),
  attachments: z.array(z.object({
    type: z.enum(["image", "video", "audio", "file", "sticker", "share"]),
    url: z.string().nullable(), // our signed copy when mirrored, else the channel's (expiring) URL
    transcript: z.string().nullable().optional(),
    mirrored: z.boolean().optional(),
  })),
  sentBy: z.enum(["contact", "user", "automation", "ai"]),
  status: z.enum(["received", "sending", "sent", "delivered", "read", "failed"]),
  sentAt: IsoDate,
  // ---- data lifecycle (ALP-65): unsent messages keep their text; edits keep every version
  deletedOnChannelAt: IsoDate.nullable().optional(),
  deletedBy: DeletedBy.nullable().optional(),
  deletionSource: DeletionSource.nullable().optional(),
  editedAt: IsoDate.nullable().optional(),
  editCount: z.number().int().optional(),
  versions: z.array(MessageVersion).optional(), // present when the message was edited or unsent
  source: IngestSource.optional(),
  // outbound sends (ALP-77): why it was not sent (readable + stable code, see OperationErrorCode) and its job
  error: z.string().nullable().optional(),
  errorCode: z.string().nullable().optional(),
  operationId: Id.nullable().optional(),
  /** Arrived while the sender was blocked in Malfak: kept, but no notification, AI or auto-reply. */
  muted: z.boolean().optional(),
});
// ---------------------------------------------------------------- AI agents: who answers (ALP-83; full agent schemas in agents.ts)
export const AgentMode = z.enum(["draft_for_approval", "auto_send"]);
/**
 * Where the agent that handles an item came from, in resolution order (first match wins):
 * rule action (`reply_with_agent` / `dm_with_agent`) → conversation assignment → post assignment → account default →
 * channel default. A conversation set to "off" never gets an agent (the old "AI agent off" toggle).
 */
export const AGENT_SOURCES = ["rule", "conversation", "post", "account", "channel"] as const;
export const AgentSource = z.enum(AGENT_SOURCES);
export const ResolvedAgent = z.object({
  agentId: Id,
  name: z.string(),
  emoji: z.string().nullable(),
  version: z.number().int(),
  mode: AgentMode,
  source: AgentSource,
  /** Id (or channel name) of the thing the assignment hangs on: rule id, conversation id, post id, account id, channel. */
  sourceRef: z.string().nullable(),
  /** Human label of that thing ("Sales rule", "@malfak_test1", "Instagram"). */
  sourceLabel: z.string().nullable(),
});
export const ConversationAgentMode = z.enum(["inherit", "assigned", "off"]);
export const ConversationAgent = z.object({
  mode: ConversationAgentMode,
  /** The manually assigned agent (mode "assigned"). */
  agentId: Id.nullable(),
  /** The agent that answers now (null = nobody: off, or no default anywhere). */
  resolved: ResolvedAgent.nullable(),
});
export const Conversation = z.object({
  id: Id,
  accountId: Id,
  channel: Channel,
  contact: Contact,
  lastMessage: Message.nullable(),
  unread: z.number(),
  inboxState: InboxState,
  assigneeId: Id.nullable(),
  windowExpiresAt: IsoDate.nullable(), // business can reply freely until then
  labels: AiLabels,
  updatedAt: IsoDate,
  aiAgent: z.boolean().optional(), // AI agent answers this conversation (toggled via updateConversation); ALP-83: true = an agent answers (mode not "off" and one resolves)
  flags: z.array(z.string()).optional(), // built-in flags + custom tag keys (ALP-65)
  /** ALP-83: who answers this conversation: a manual assignment, inherited (account / channel default) or off. */
  agent: ConversationAgent.optional(),
  /** The contact is blocked in Malfak for this account: out of the default inbox (the "Muted" filter shows it). */
  muted: z.boolean().optional(),
});
export const InboxItem = z.discriminatedUnion("type", [
  z.object({ type: z.literal("comment"), comment: Comment, publication: Publication.pick({ id: true, permalink: true, postId: true }).nullable() }),
  z.object({ type: z.literal("conversation"), conversation: Conversation }),
]);
export const InboxQuery = z.object({
  state: InboxState.optional(),
  channel: Channel.optional(),
  accountId: Id.optional(),
  type: z.enum(["comment", "conversation"]).optional(),
  intent: AiLabels.shape.intent.unwrap().optional(),
  sentiment: AiLabels.shape.sentiment.unwrap().optional(),
  assignee: z.enum(["me", "unassigned", "anyone"]).optional(),
  q: z.string().optional(),
  semantic: QueryBool.optional(), // meaning search (SemanticIndex: Dify Knowledge, lexical fallback); "false" stays false
  contactId: Id.optional(), // contact profile: this person's comments and conversations
  showDeleted: QueryBool.optional(), // include items deleted on the channel (hidden by default)
  onlyDeleted: QueryBool.optional(), // only items deleted on the channel
  flag: FlagKey.optional(), // items carrying this built-in flag or custom tag
  archived: z.enum(["exclude", "include", "only"]).optional(), // ALP-72: default exclude
  /** Items of people blocked in Malfak (their muted DMs and comments): default exclude; `only` = the "Muted" filter. */
  muted: z.enum(["exclude", "include", "only"]).optional(),
});
/**
 * How a `semantic=true` search was answered (QA F18): `unavailable` = the meaning index could not answer (unreachable, or
 * nothing indexed) and the results are keyword matches only; `degraded` = part of the org's items failed to index.
 */
export const SemanticSearchInfo = z.object({
  state: z.enum(["ok", "degraded", "unavailable"]),
  reason: z.enum(["unreachable", "index_errors", "search_failed"]).nullable(),
});
export const ReplyRequest = z.object({ text: z.string().min(1).max(2200), private: z.boolean().default(false) });
export const SendMessageRequest = z.object({
  text: z.string().max(1000).optional(),
  quickReplies: z.array(z.object({ title: z.string().max(20), payload: z.string() })).max(13).optional(),
  buttons: z.array(z.object({ title: z.string().max(20), url: z.string().url().optional(), payload: z.string().optional() })).max(3).optional(),
  audioMediaId: Id.optional(),
});
export const BulkCommentAction = z.object({
  commentIds: z.array(Id).min(1).max(5000),
  action: z.enum(["hide", "unhide", "delete", "reply", "mark_done", "assign", "archive", "unarchive"]),
  text: z.string().max(2200).optional(),
  assigneeId: Id.optional(),
});

// ---------------------------------------------------------------- automation
export const Condition: z.ZodType<ConditionT> = z.lazy(() =>
  z.discriminatedUnion("op", [
    z.object({ op: z.literal("all"), of: z.array(Condition) }),
    z.object({ op: z.literal("any"), of: z.array(Condition) }),
    z.object({ op: z.literal("not"), of: Condition }),
    z.object({ op: z.literal("keyword"), words: z.array(z.string()).min(1), mode: z.enum(["contains", "exact"]).default("contains") }),
    z.object({ op: z.literal("regex"), pattern: z.string() }),
    z.object({ op: z.literal("language"), in: z.array(z.string()) }),
    z.object({ op: z.literal("intent"), in: z.array(AiLabels.shape.intent.unwrap()), minConfidence: z.number().min(0).max(1).default(0.7) }),
    z.object({ op: z.literal("sentiment"), in: z.array(AiLabels.shape.sentiment.unwrap()), minConfidence: z.number().min(0).max(1).optional() }), // ALP-83: optional confidence
    z.object({ op: z.literal("ai_prompt"), describe: z.string().min(3) }), // natural-language condition evaluated by AI
    z.object({ op: z.literal("first_time_contact") }),
    z.object({ op: z.literal("is_reply") }),
    z.object({ op: z.literal("has_link") }),
    z.object({ op: z.literal("commenter_marked"), in: z.array(CommenterMark).min(1) }), // ALP-72
    // ALP-83: the item carries any of these built-in flags / custom tags / AI categories
    z.object({ op: z.literal("has_tag"), in: z.array(FlagKey).min(1).max(20) }),
    // ALP-83: the org's own AI classification labels (custom categories), decided by the comment classifier
    z.object({ op: z.literal("custom_category"), in: z.array(FlagKey).min(1).max(20), minConfidence: z.number().min(0).max(1).default(0.7) }),
    // the author is blocked in Malfak for this account. Only rules that use it run for blocked people (every other rule
    // is skipped for them); even these never reply or call AI for a blocked person.
    z.object({ op: z.literal("commenter_blocked") }),
  ]),
);
export type ConditionT =
  | { op: "all" | "any"; of: ConditionT[] }
  | { op: "not"; of: ConditionT }
  | { op: "keyword"; words: string[]; mode: "contains" | "exact" }
  | { op: "regex"; pattern: string }
  | { op: "language"; in: string[] }
  | { op: "intent"; in: string[]; minConfidence: number }
  | { op: "sentiment"; in: string[]; minConfidence?: number | undefined }
  | { op: "ai_prompt"; describe: string }
  | { op: "first_time_contact" | "is_reply" | "has_link" }
  | { op: "commenter_marked"; in: ("harmful" | "troll" | "vip")[] }
  | { op: "has_tag"; in: string[] }
  | { op: "custom_category"; in: string[]; minConfidence: number }
  | { op: "commenter_blocked" };
export const RuleAction = z.discriminatedUnion("type", [
  z.object({ type: z.literal("reply_public"), template: z.string().max(2200), aiPersonalize: z.boolean().default(false) }),
  z.object({ type: z.literal("reply_private"), template: z.string().max(1000), aiPersonalize: z.boolean().default(false) }),
  z.object({ type: z.literal("send_message"), template: z.string().max(1000), aiPersonalize: z.boolean().default(false) }),
  z.object({ type: z.literal("ai_agent_reply"), agent: z.string().default("dm-agent") }),
  z.object({ type: z.literal("hide") }),
  z.object({ type: z.literal("delete") }),
  z.object({ type: z.literal("tag"), tags: z.array(z.string()).min(1) }),
  z.object({ type: z.literal("assign"), assigneeId: Id }),
  z.object({ type: z.literal("notify"), channels: z.array(z.enum(["in_app", "email", "slack", "telegram"])) }),
  // ALP-65: add built-in flags / custom tags to the matched item (or to its contact)
  z.object({ type: z.literal("flag"), flags: z.array(FlagKey).min(1).max(10), target: z.enum(["item", "contact"]).default("item") }),
  // ALP-72: mark the commenter (contact) and fetch their public profile
  z.object({ type: z.literal("mark_commenter"), mark: CommenterMark }),
  z.object({ type: z.literal("enrich_commenter") }),
  // ALP-83: answer with one of the org's AI agents (persona, instructions, knowledge, limits); the agent's mode decides draft vs send
  z.object({ type: z.literal("reply_with_agent"), agentId: Id, private: z.boolean().default(false) }),
  z.object({ type: z.literal("dm_with_agent"), agentId: Id }),
]);
export const Rule = z.object({
  id: Id,
  name: z.string().min(1),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  scope: z.object({ accountId: Id.nullable(), publicationId: Id.nullable() }), // null account = all accounts
  trigger: z.enum(["comment.created", "message.received", "mention", "story.reply", "postback"]),
  conditions: Condition,
  actions: z.array(RuleAction).min(1),
  mode: z.enum(["auto", "approve", "suggest"]),
  limits: z.object({ perHour: z.number().int().min(1).max(500), quietHours: z.object({ from: z.string(), to: z.string(), tz: z.string() }).nullable() }),
  priority: z.number().int(),
  isActive: z.boolean(),
  stats: z.object({ matched7d: z.number(), executed7d: z.number(), pending: z.number() }),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export const RuleInput = Rule.omit({ id: true, stats: true, createdAt: true, updatedAt: true });
export const RuleSimulation = z.object({
  ruleId: Id.optional(),
  rule: RuleInput.optional(),
  sampleSize: z.number().int().min(1).max(200).default(50),
});
/** A compact reference to the post an item belongs to (quick views: thumbnails, filters, peeks). */
export const PostRef = z.object({
  postId: Id,
  publicationId: Id.nullable(),
  accountId: Id.nullable(),
  caption: z.string(),
  thumbnailUrl: z.string().nullable(),
  permalink: z.string().nullable(),
});
export const RunResult = z.enum(["executed", "pending_approval", "skipped", "failed", "suggested"]);
export const AutomationRun = z.object({
  id: Id,
  ruleId: Id.nullable(),
  ruleName: z.string().nullable(),
  eventRef: z.string(),
  matched: z.boolean(),
  labels: AiLabels.nullable(),
  action: z.string().nullable(),
  result: RunResult,
  traceUrl: z.string().nullable(), // Langfuse
  createdAt: IsoDate,
  reason: z.string().nullable().optional(), // why it was skipped/failed (e.g. "automation paused by platform admin")
  // quick-view context (ALP-71, optional/additive)
  accountId: Id.nullable().optional(),
  post: PostRef.nullable().optional(), // the post the event happened on (comments only)
  error: z.string().nullable().optional(), // why a failed run failed (provider error / policy)
  /** Stable code of `error` (OPERATION_ERROR_CODES, e.g. `messaging_window_closed`); the UI localizes it like jobs. */
  errorCode: z.string().nullable().optional(),
  retryOf: Id.nullable().optional(), // this run re-executed run `retryOf`
});
/** Run history filters (all optional; `result` and `action` accept comma-separated lists). */
export const RunsQuery = z.object({
  ruleId: Id.optional(),
  postId: Id.optional(),
  accountId: Id.optional(),
  result: z.string().optional(),
  action: z.string().optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
/** One step of a run's timeline: event → conditions → AI → approval → action → provider response. */
export const RunStep = z.object({
  kind: z.enum(["event", "conditions", "ai", "approval", "action", "provider", "retry"]),
  status: z.enum(["ok", "skipped", "pending", "failed", "info"]),
  at: IsoDate.nullable(),
  title: z.string(),
  detail: z.string().nullable(),
  data: z.record(z.string(), z.unknown()).optional(),
});
export const RunDetail = AutomationRun.extend({
  ruleVersion: z.number().int().nullable(),
  event: z.object({ type: z.enum(["comment", "message"]), id: Id.nullable(), text: z.string().nullable(), author: z.string().nullable(), at: IsoDate.nullable() }),
  timeline: z.array(RunStep),
  retriable: z.boolean(), // failed and the event still exists
  retries: z.array(z.object({ at: IsoDate, by: Id.nullable(), operationId: Id.nullable() })),
});
export const RetryRunsRequest = z.object({ runIds: z.array(Id).min(1).max(100) });
export const RetryRunsResult = z.object({
  queued: z.array(z.object({ runId: Id, operationId: Id })),
  skipped: z.array(z.object({ runId: Id, reason: z.string() })),
});
/** Why a rule card shows ⚠ (computed server-side; postIds are the affected posts for the peek). */
export const RuleIssueKind = z.enum(["paused", "no_matching_posts", "failing_runs", "approval_backlog", "account_disconnected"]);
export const RuleHealth = z.object({
  ruleId: Id,
  scopePost: PostRef.nullable(), // set when the rule is limited to one post
  matchingPosts: z.number().int().nullable(), // published posts in scope (null = not post-bound, e.g. DMs)
  issues: z.array(z.object({ kind: RuleIssueKind, count: z.number().int(), postIds: z.array(Id) })),
});
/**
 * ALP-74: result of executing an approved action (ExecuteApprovedAction / the waiting inbound workflow). `errorCode` is a
 * stable code (e.g. `channel_reauth_required`, `messaging_window_closed`) the UI maps to a localized message.
 */
export const ApprovalExecution = z.object({
  status: z.enum(["executed", "failed"]),
  error: z.string().nullable(),
  errorCode: z.string().nullable(),
  at: IsoDate.nullable(),
});
export const Approval = z.object({
  id: Id,
  kind: z.enum(["reply_public", "reply_private", "send_message", "hide", "delete", "bulk"]),
  proposedBy: z.enum(["rule", "assistant", "user"]),
  ruleId: Id.nullable(),
  target: z.object({ type: z.enum(["comment", "conversation", "bulk"]), id: Id.nullable(), preview: z.string() }),
  draft: z.string().nullable(),
  reason: z.string().nullable(),
  status: z.enum(["pending", "approved", "edited", "rejected", "expired"]),
  expiresAt: IsoDate.nullable(),
  createdAt: IsoDate,
  // quick-view context (ALP-71, optional/additive)
  accountId: Id.nullable().optional(),
  post: PostRef.nullable().optional(),
  // ALP-74: the automation run this approval belongs to, and what happened when the approved action ran
  runId: Id.nullable().optional(),
  execution: ApprovalExecution.nullable().optional(),
  /** ALP-83: the AI agent (and its version) that drafted this reply. */
  agent: z.object({ id: Id, name: z.string(), emoji: z.string().nullable(), version: z.number().int() }).nullable().optional(),
  /** DM approvals (target conversation): when its 24h free-reply window closes; null = closed. Sending after that fails
   *  with `messaging_window_closed`, so the card warns before approving (QA F19). Omitted on other kinds / SSE paths. */
  windowExpiresAt: IsoDate.nullable().optional(),
});
export const ApprovalDecision = z.object({ decision: z.enum(["approve", "reject"]), editedText: z.string().max(2200).optional() });
/** Approval queue filters: `status` and `kind` accept comma-separated lists; `from`/`to` bound createdAt (age). */
export const ApprovalsQuery = z.object({
  status: z.string().optional(),
  postId: Id.optional(),
  accountId: Id.optional(),
  ruleId: Id.optional(),
  kind: z.string().optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export const BatchApprovalDecision = z.object({ ids: z.array(Id).min(1).max(100), decision: z.enum(["approve", "reject"]) });
export const BatchApprovalResult = z.object({
  results: z.array(z.object({ id: Id, ok: z.boolean(), status: z.enum(["pending", "approved", "edited", "rejected", "expired"]).nullable(), error: z.string().nullable() })),
});

// ---------------------------------------------------------------- analytics
export const AccountOverview = z.object({
  accountId: Id,
  range: z.object({ from: IsoDate, to: IsoDate }),
  metrics: z.array(Metric), // reach, views, accounts_engaged, total_interactions, likes, comments, shares, saves, followers…
  series: z.record(z.string(), z.array(SeriesPoint)),
  topPublications: z.array(Publication),
  sentiment: z.object({ positive: z.number(), neutral: z.number(), negative: z.number() }),
  automation: z.object({ handled: z.number(), autoReplied: z.number(), hidden: z.number(), minutesSaved: z.number() }),
});
export const BestTimes = z.object({ heatmap: z.array(z.array(z.number())), timezone: z.string(), suggestions: z.array(z.object({ day: z.number(), hour: z.number(), score: z.number() })) });
export const Demographics = z.object({ byCountry: z.record(z.string(), z.number()), byCity: z.record(z.string(), z.number()), byAge: z.record(z.string(), z.number()), byGender: z.record(z.string(), z.number()) });

// ---------------------------------------------------------------- AI features
export const CaptionRequest = z.object({ mediaId: Id.optional(), brief: z.string().max(1000).optional(), tone: z.enum(["casual", "professional", "playful", "luxury", "bold"]).default("casual"), language: Locale.default("ar"), withHashtags: z.boolean().default(true), occasion: z.object({ eventId: Id }).optional() }); // occasion: a calendar event whose context and mood shape the draft
export const CaptionResult = z.object({ options: z.array(z.object({ caption: z.string(), hashtags: z.array(z.string()) })), score: z.number().nullable() });
export const PostScore = z.object({ score: z.number().min(0).max(100), checks: z.array(z.object({ key: z.string(), ok: z.boolean(), tip: z.string() })) });
export const ImageGenRequest = z.object({ prompt: z.string().min(3).max(2000), size: z.enum(["1024x1024", "1024x1536", "1536x1024"]).default("1024x1024") });
export const AssistantMessage = z.object({
  id: Id,
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  staged: z.array(Approval).default([]),
  createdAt: IsoDate,
});
export const AssistantAsk = z.object({ text: z.string().min(1).max(4000), conversationId: z.string().nullable(), voice: z.boolean().default(false) });

// ---------------------------------------------------------------- platform
export const BatchJob = z.object({
  id: Id,
  kind: z.enum(["bulk_comment_action", "backfill_sync", "reembed", "reclassify", "report", "cross_post"]),
  status: z.enum(["queued", "running", "paused", "succeeded", "failed", "cancelled"]),
  total: z.number(),
  done: z.number(),
  failed: z.number(),
  createdAt: IsoDate,
  finishedAt: IsoDate.nullable(),
});
export const Notification = z.object({ id: Id, kind: z.string(), title: z.string(), body: z.string().nullable(), href: z.string().nullable(), readAt: IsoDate.nullable(), createdAt: IsoDate });

// ---------------------------------------------------------------- flags & tags (ALP-65)
/** A flag/tag definition. Built-ins have `builtIn: true` and `id: null` (labels are localized by key in the UI). */
/**
 * ALP-83: `kind: "category"` = a custom AI category. The comment classifier labels items with it (with a confidence)
 * using the description + examples; items at/above `minConfidence` carry the key as a flag (added_by_type "ai"), so a
 * category filters, counts and charts exactly like a tag. Built-in flags and plain tags are `kind: "tag"`.
 */
export const TagKind = z.enum(["tag", "category"]);
export const DEFAULT_CATEGORY_CONFIDENCE = 0.7;
export const Tag = z.object({
  id: Id.nullable(),
  key: FlagKey,
  label: z.string(),
  color: z.string().nullable(),
  builtIn: z.boolean(),
  createdAt: IsoDate.nullable(),
  kind: TagKind.optional(), // absent = "tag" (built-ins, older rows)
  description: z.string().nullable().optional(),
  examples: z.array(z.string()).optional(),
  accountId: Id.nullable().optional(), // categories: limit to one account (null = every account)
  minConfidence: z.number().min(0).max(1).optional(), // categories: flag the item at/above this confidence
});
const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const TagInput = z.object({
  label: z.string().trim().min(1).max(40),
  key: FlagKey.optional(), // derived from the label when omitted
  color: HexColor.nullable().optional(),
  kind: TagKind.default("tag"),
  description: z.string().trim().max(500).nullable().optional(),
  examples: z.array(z.string().trim().min(1).max(300)).max(10).optional(),
  accountId: Id.nullable().optional(),
  minConfidence: z.number().min(0).max(1).optional(),
});
export const TagPatch = z.object({
  label: z.string().trim().min(1).max(40).optional(),
  color: HexColor.nullable().optional(),
  kind: TagKind.optional(),
  description: z.string().trim().max(500).nullable().optional(),
  examples: z.array(z.string().trim().min(1).max(300)).max(10).optional(),
  accountId: Id.nullable().optional(),
  minConfidence: z.number().min(0).max(1).optional(),
});
/** ALP-83 (measurable tags): how many items carry each flag / tag / category, for a post, an account, a rule or the org. */
export const TagCountsQuery = z.object({
  postId: Id.optional(),
  publicationId: Id.optional(),
  accountId: Id.optional(),
  /** Only flags this rule added (entity_flags.added_by = rule id). */
  ruleId: Id.optional(),
  entityType: FlaggableEntity.default("comment"),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
});
export const TagCount = z.object({ key: z.string(), count: z.number().int() });
/** `total` = distinct items carrying at least one of the counted keys (not the sum: an item can carry several). */
export const TagCountsResult = z.object({ items: z.array(TagCount), total: z.number().int() });
/** ALP-83: "This rule tagged N comments (this week / all time), across these posts". */
export const RuleTagStats = z.object({
  ruleId: Id,
  /** Flag keys the rule adds (its `flag` actions). */
  tags: z.array(z.string()),
  week: z.number().int(),
  allTime: z.number().int(),
  byTag: z.array(TagCount),
  /** Top posts by tagged comments (all time). */
  posts: z.array(z.object({ post: PostRef, count: z.number().int() })).max(10),
});
export const SetFlagsRequest = z
  .object({
    entityType: FlaggableEntity,
    ids: z.array(Id).min(1).max(5000),
    add: z.array(FlagKey).max(20).default([]),
    remove: z.array(FlagKey).max(20).default([]),
  })
  .refine((b) => b.add.length + b.remove.length > 0, { message: "add or remove at least one flag", path: ["add"] });
export const SetFlagsResult = z.object({
  updated: z.number(),
  /** Flags after the change, only when at most 200 ids were sent. */
  items: z.array(z.object({ id: Id, flags: z.array(z.string()) })),
  jobId: Id.nullable(), // bulk changes (more than 1 id) are recorded as a finished job
});
export const FlagCounts = z.object({
  counts: z.record(z.string(), z.object({ comment: z.number(), conversation: z.number(), contact: z.number(), total: z.number() })),
});

// ---------------------------------------------------------------- retention & per-contact data (ALP-65)
export const RetentionSettings = z.object({
  mode: z.enum(["forever", "days"]), // default: forever
  days: z.number().int().min(30).max(3650).nullable(),
  updatedAt: IsoDate.nullable(),
});
export const RetentionInput = z
  .object({ mode: z.enum(["forever", "days"]), days: z.number().int().min(30).max(3650).nullable().optional() })
  .refine((r) => r.mode === "forever" || typeof r.days === "number", { message: "days is required when mode is days", path: ["days"] });
/** GDPR-style export of everything Malfak stores about one contact (JSON download). */
export const ContactExport = z.object({
  exportedAt: IsoDate,
  contact: Contact,
  comments: z.array(Comment),
  conversations: z.array(z.object({ id: Id, accountId: Id, channel: Channel, lastMessageAt: IsoDate.nullable(), messages: z.array(Message) })),
  flags: z.array(z.string()),
});
export const ContactDeletion = z.object({
  deleted: z.object({ contacts: z.number(), comments: z.number(), conversations: z.number(), messages: z.number(), flags: z.number() }),
});

// ---------------------------------------------------------------- recovery (ALP-66)
export const Incident = z.object({
  id: Id,
  kind: z.enum(["webhooks_gap"]),
  status: z.enum(["open", "resolved"]),
  accountId: Id.nullable(),
  title: z.string(),
  details: z.record(z.string(), z.unknown()),
  occurrences: z.number(),
  openedAt: IsoDate,
  lastSeenAt: IsoDate,
  resolvedAt: IsoDate.nullable(),
});
export const SyncHealth = z.object({
  accountId: Id,
  cadence: z.enum(["active", "quiet"]), // active: reconcile every 10 min; quiet: hourly
  lastWebhookAt: IsoDate.nullable(),
  lastReconcileAt: IsoDate.nullable(),
  lastRecoveredAt: IsoDate.nullable(),
  recoveredLastRun: z.number(),
  recoveredTotal: z.number(),
  openIncident: Incident.nullable(),
});
export const ReloadCommentsRequest = z.object({ maxPages: z.number().int().min(1).max(200).default(50) });
export const ResyncAccountRequest = z.object({
  maxMedia: z.number().int().min(1).max(1000).default(100),
  maxCommentPagesPerMedia: z.number().int().min(1).max(200).default(20),
  includeMessages: z.boolean().default(true),
});

// ---------------------------------------------------------------- jobs center (ALP-67)
export const JobStatus = z.enum(["queued", "running", "paused", "succeeded", "failed", "cancelled"]);
export const JobCategory = z.enum(["sync", "bulk", "ai", "publish", "export", "other"]);
export const JobTarget = z.object({
  type: z.enum(["account", "publication", "post", "contact", "comments", "comment", "conversation", "org"]),
  id: z.string().nullable(),
  label: z.string().nullable(),
});
/**
 * Every `operations.kind` the API or worker writes. The web app must label each one in every locale (a test enforces it).
 * `comment.*` / `message.send` are the single outbound sends (ALP-77: every send is a job).
 */
export const JOB_KINDS = [
  "resync_post", "resync_account", "backfill_sync", "backfill_account", "reconcile", "bulk_comment_action", "bulk_flag", "bulk_archive",
  "publish", "cross_post", "contact_export", "contact_delete", "enrich_contact", "reembed", "reclassify", "report", "semantic_reindex",
  "calendar_refresh", "automation.run.retry", "account.sync", "bulk_undo", "usage_reconcile", "ai_key_provision",
  // People bulk moderation (people.ts): one job per action over the selected people
  "people.flag", "people.mark", "people.block", "people.unblock", "people.hide_comments", "people.unhide_comments",
  "people.archive_comments", "people.unarchive_comments", "people.delete_comments", "people.export",
  "comment.reply", "comment.private_reply", "comment.delete", "comment.hide", "comment.unhide", "message.send",
  // every AI generation is a job (ai-jobs.ts)
  "ai.image", "ai.image_edit", "ai.caption", "ai.score", "ai.rule", "ai.tts", "ai.stt", "ai.occasion",
  // ALP-83: one AI-agent reply (draft or send), a batch of agent drafts (dashboard "apply to these items" / Copilot)
  "agent.reply", "agent.batch",
  "media_refresh", "media_repair", "post_reload", // channel media mirroring + reload from Instagram (media.ts)
] as const;
/** Every long operation (operations row + Temporal workflow) as the jobs center sees it. */
export const Job = z.object({
  id: Id,
  kind: z.string(), // resync_post | resync_account | bulk_comment_action | bulk_flag | publish | contact_export | backfill_sync | ...
  category: JobCategory,
  title: z.string(), // English fallback; the UI localizes by kind + target
  status: JobStatus,
  total: z.number().nullable(),
  done: z.number(),
  failed: z.number(),
  skipped: z.number(),
  progress: z.number().min(0).max(1).nullable(), // null = indeterminate
  etaSeconds: z.number().nullable(),
  target: JobTarget.nullable(),
  error: z.string().nullable(),
  /** Stable code of the failure (e.g. `channel_reauth_required` → "reconnect Instagram"); null when none (ALP-77). */
  errorCode: z.string().nullable().optional(),
  result: z.unknown().optional(),
  createdBy: Id.nullable(),
  createdAt: IsoDate,
  startedAt: IsoDate.nullable(),
  updatedAt: IsoDate,
  finishedAt: IsoDate.nullable(),
  retryOf: Id.nullable(),
  workflowId: z.string().nullable(),
  can: z.object({ cancel: z.boolean(), pause: z.boolean(), resume: z.boolean(), retry: z.boolean(), report: z.boolean() }),
});
export const JobItem = z.object({ ref: z.string(), status: z.enum(["ok", "failed", "skipped"]), error: z.string().nullable(), attempts: z.number(), updatedAt: IsoDate });
export const JobsQuery = z.object({
  status: z.enum(["active", "finished", "failed", "all"]).default("all"),
  kind: z.string().max(60).optional(),
  category: JobCategory.optional(),
});
export const JobCounts = z.object({ active: z.number(), running: z.number(), paused: z.number(), queued: z.number(), failed24h: z.number(), finished24h: z.number() });

// ---------------------------------------------------------------- comment moderation & commenter profiles (ALP-72)
export const CommentSort = z.enum(["newest", "oldest", "replies", "toxicity"]);
/** Post page comment list filters (`comments` route). Pinned comments come first unless `pinned` filters them. */
export const PostCommentsQuery = z.object({
  q: z.string().max(200).optional(),
  semantic: QueryBool.optional(), // meaning search (SemanticIndex: Dify Knowledge, intent-aware lexical fallback)
  status: z.enum(["visible", "hidden", "deleted"]).optional(),
  sentiment: AiLabels.shape.sentiment.unwrap().optional(),
  intent: AiLabels.shape.intent.unwrap().optional(),
  flag: FlagKey.optional(),
  ruleId: Id.optional(), // a rule acted on it (automation_runs)
  ruleAction: z.string().max(40).optional(), // action taken by a rule, e.g. reply_public | hide
  replied: QueryBool.optional(), // true = the account replied (public or private); false = unreplied
  showDeleted: QueryBool.optional(),
  onlyDeleted: QueryBool.optional(),
  archived: z.enum(["exclude", "include", "only"]).default("exclude"),
  pinned: QueryBool.optional(),
  topLevel: QueryBool.optional(), // only top-level comments (replies come with the thread)
  sort: CommentSort.default("newest"),
});
export const CommentPatch = z.object({
  status: z.enum(["visible", "hidden"]).optional(),
  inboxState: InboxState.optional(),
  assigneeId: Id.nullable().optional(),
  archived: z.boolean().optional(), // ALP-72
  pinned: z.boolean().optional(), // ALP-72
});
export const CommenterCounts = z.object({
  comments: z.number(),
  negative: z.number(),
  toxic: z.number(), // labels.toxicity >= 0.5
  spam: z.number(),
  flagged: z.number(), // comments carrying any flag/tag
  hidden: z.number(),
  deleted: z.number(),
  archived: z.number(),
});
/** One person who commented on an account (People view). */
export const Commenter = z.object({
  contactId: Id.nullable(),
  externalId: z.string(),
  username: z.string(),
  displayName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  mark: CommenterMark.nullable(),
  flags: z.array(z.string()),
  profile: ContactProfile.nullable(),
  block: ContactBlockState.nullable().optional(), // Block in Malfak that applies to this account (or to all accounts)
  counts: CommenterCounts,
  firstCommentAt: IsoDate,
  lastCommentAt: IsoDate,
  profileUrl: z.string().url(), // https://instagram.com/<username>
});
export const CommentersQuery = z.object({
  q: z.string().max(100).optional(),
  mark: z.enum(["harmful", "troll", "vip", "any", "none"]).optional(),
  sort: z.enum(["recent", "comments", "negative", "toxic"]).default("recent"),
  blocked: QueryBool.optional(), // true = only people blocked for this account (or all accounts); false = not blocked
  flagged: QueryBool.optional(), // true = the person carries a flag or tag; false = none
  has: z.enum(["hidden", "deleted", "hidden_or_deleted"]).optional(), // at least one hidden / deleted comment
});
/** Everything the comment quick panel needs in one call. */
export const CommentDetail = z.object({
  comment: Comment,
  publication: z.object({ id: Id, permalink: z.string().nullable(), postId: Id.nullable(), caption: z.string().nullable(), mediaUrl: z.string().nullable() }).nullable(),
  thread: z.array(Comment), // parent (when a reply) + every reply, oldest first; includes the account's own replies
  runs: z.array(AutomationRun), // rule runs that touched this comment
  commenter: Commenter.nullable(),
  can: z.object({ reply: z.boolean(), privateReply: z.boolean(), privateReplyUntil: IsoDate.nullable(), hide: z.boolean(), delete: z.boolean() }),
  /** Everything that happened to the comment, newest first (ALP-84; at most 30 entries). */
  history: z.array(CommentEvent).optional(),
});
export const ContactMarkInput = z.object({ mark: CommenterMark.nullable() });
export const ContactCommentsAction = z.object({
  action: z.enum(["archive", "unarchive", "hide", "unhide"]),
  accountId: Id.optional(), // limit to one account
});

/** Server-sent events pushed to the web app (GET /v1/orgs/:org/events). */
export const ServerEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("inbox.item"), item: z.unknown() }),
  z.object({ type: z.literal("comment.updated"), comment: Comment }),
  z.object({ type: z.literal("message.created"), message: Message }),
  // ALP-77: an outbound message changed state (sending → sent / failed with a reason); same id, the UI upserts
  z.object({ type: z.literal("message.updated"), message: Message }),
  z.object({ type: z.literal("approval.created"), approval: Approval }),
  z.object({
    type: z.literal("operation.updated"), operationId: Id, status: z.string(), done: z.number().optional(), total: z.number().nullable().optional(),
    failed: z.number().optional(), kind: z.string().optional(), // jobs center (ALP-67)
    // terminal failures: readable reason + stable code (`channel_reauth_required` → Reconnect), ALP-77
    error: z.string().nullable().optional(), errorCode: z.string().nullable().optional(),
  }),
  z.object({ type: z.literal("publication.updated"), publication: Publication }),
  /** Debounced server-side from the events above: widgets reading these datasets re-query (plan 14 §10). */
  z.object({ type: z.literal("dataset.changed"), datasets: z.array(z.string().max(40)).max(20), at: IsoDate }),
  /** A dashboard (or one of its widgets) changed: other open views refetch it. */
  z.object({ type: z.literal("dashboard.updated"), dashboardId: Id, deleted: z.boolean().optional() }),
  // audience: user ids the notification was created for (absent = everyone in the org); others only refresh their tray
  z.object({ type: z.literal("notification"), notification: Notification, audience: z.array(Id).optional(), priority: z.enum(["normal", "high"]).optional() }),
]);

export type User = z.infer<typeof User>;
export type Org = z.infer<typeof Org>;
export type Me = z.infer<typeof Me>;
export type Channel = z.infer<typeof Channel>;
export type ChannelCapabilities = z.infer<typeof ChannelCapabilities>;
export type ChannelAccount = z.infer<typeof ChannelAccount>;
export type BackfillProgress = z.infer<typeof BackfillProgress>;
export type BackfillGap = z.infer<typeof BackfillGap>;
export type DataDeletionStatus = z.infer<typeof DataDeletionStatus>;
export type OAuthErrorCode = z.infer<typeof OAuthErrorCode>;
export type Contact = z.infer<typeof Contact>;
export type Post = z.infer<typeof Post>;
export type Publication = z.infer<typeof Publication>;
export type Comment = z.infer<typeof Comment>;
export type CommentActor = z.infer<typeof CommentActor>;
export type CommentEvent = z.infer<typeof CommentEvent>;
export type CommentEventKind = z.infer<typeof CommentEventKind>;
export type Message = z.infer<typeof Message>;
export type Conversation = z.infer<typeof Conversation>;
export type InboxItem = z.infer<typeof InboxItem>;
export type SemanticSearchInfo = z.infer<typeof SemanticSearchInfo>;
export type Rule = z.infer<typeof Rule>;
export type RuleInput = z.infer<typeof RuleInput>;
export type RuleAction = z.infer<typeof RuleAction>;
export type AutomationRun = z.infer<typeof AutomationRun>;
export type Approval = z.infer<typeof Approval>;
export type PostRef = z.infer<typeof PostRef>;
export type RunDetail = z.infer<typeof RunDetail>;
export type RunStep = z.infer<typeof RunStep>;
export type RuleHealth = z.infer<typeof RuleHealth>;
export type AccountOverview = z.infer<typeof AccountOverview>;
export type BatchJob = z.infer<typeof BatchJob>;
export type ServerEvent = z.infer<typeof ServerEvent>;
export type Tag = z.infer<typeof Tag>;
export type TagKind = z.infer<typeof TagKind>;
export type TagCountsResult = z.infer<typeof TagCountsResult>;
export type RuleTagStats = z.infer<typeof RuleTagStats>;
export type ResolvedAgent = z.infer<typeof ResolvedAgent>;
export type ConversationAgent = z.infer<typeof ConversationAgent>;
export type AgentSource = z.infer<typeof AgentSource>;
export type Incident = z.infer<typeof Incident>;
export type SyncHealth = z.infer<typeof SyncHealth>;
export type Job = z.infer<typeof Job>;
export type JobItem = z.infer<typeof JobItem>;
export type JobCounts = z.infer<typeof JobCounts>;
export type RetentionSettings = z.infer<typeof RetentionSettings>;
export type ContactExport = z.infer<typeof ContactExport>;
export type MessageVersion = z.infer<typeof MessageVersion>;
export type Commenter = z.infer<typeof Commenter>;
export type CommentDetail = z.infer<typeof CommentDetail>;
export type ContactProfile = z.infer<typeof ContactProfile>;
export type ContactBlockState = z.infer<typeof ContactBlockState>;
